import { lazy, Suspense, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  climateOf,
  type Asset,
  type Device,
  type DeviceChannel,
  type TelemetryReading,
} from "@/contracts";

import {
  assetOptionsQuery,
  deactivateDeviceMutation,
  deviceQuery,
  featuresQuery,
  seriesPointsAsReadings,
  telemetryQuery,
  telemetrySeriesQuery,
} from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { useApiClient } from "@/lib/api-context";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import {
  formatBattery,
  formatCurrent,
  formatCurrentRange,
  formatDateTime,
  formatDevEui,
  formatDistance,
  formatDistanceRange,
  formatDoorOpen,
  formatLeaking,
  formatOccupied,
  formatHumidity,
  formatTemperatureRange,
  formatRelative,
  formatTemperature,
} from "@/lib/format";
import { formatOccupancy } from "@/features/assets/asset-labels";
import { InstallationCard } from "@/features/gateways/installation-card";
import { ConnectivityBadge, DeviceStatusBadge, RangeStateBadge } from "./device-badges";
import { ChannelConfigDialog } from "./channel-config-dialog";
import { CHANNEL_KIND_LABELS } from "./device-labels";
import { DeviceEditForm } from "./device-edit-form";
import { SimulateUplinkDialog } from "./simulate-uplink-dialog";

/**
 * The charting library is a third of the bundle and only this screen needs
 * it, so it is fetched when someone opens a device rather than on the way to
 * the login form.
 */
const TelemetryChart = lazy(() =>
  import("./telemetry-chart").then((module) => ({ default: module.TelemetryChart })),
);

const CoverageChart = lazy(() =>
  import("./coverage-chart").then((module) => ({ default: module.CoverageChart })),
);

const PERIODS = [
  { hours: 6, label: "6 h" },
  { hours: 24, label: "24 h" },
  { hours: 24 * 7, label: "7 d" },
  { hours: 24 * 30, label: "30 d" },
] as const;

export function DeviceDetailPage() {
  const { t } = useTranslation("pages");
  const { t: tDevices } = useTranslation("devices");
  const { id = "" } = useParams<{ id: string }>();
  const client = useApiClient();
  const { user } = useAuth();
  const canEdit = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);

  const [hours, setHours] = useState<number>(24);
  const [editing, setEditing] = useState(false);
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);
  const [simulating, setSimulating] = useState(false);

  const device = useQuery(deviceQuery(client, id));
  const features = useQuery(featuresQuery(client));
  const useSeries = hours > 24;
  const seriesBucket = hours > 24 * 7 ? ("1d" as const) : ("1h" as const);
  // Short windows use raw points for both chart and table. Longer windows
  // downsample for the chart and keep a 24 h raw page for the latest-readings
  // table so the operator still sees individual uplinks.
  const telemetry = useQuery(telemetryQuery(client, id, useSeries ? 24 : hours));
  const series = useQuery({
    ...telemetrySeriesQuery(client, id, hours, seriesBucket),
    enabled: useSeries && Boolean(id),
  });
  const assets = useQuery({ ...assetOptionsQuery(client), enabled: canEdit });
  const chartReadings: readonly TelemetryReading[] = useMemo(() => {
    if (useSeries) {
      return series.data ? seriesPointsAsReadings(series.data) : [];
    }
    return telemetry.data?.data ?? [];
  }, [useSeries, series.data, telemetry.data]);
  const chartPending = useSeries ? series.isPending : telemetry.isPending;
  const chartError = useSeries ? series.error : telemetry.error;
  const chartRefetch = useSeries ? series.refetch : telemetry.refetch;
  const canSimulate = canEdit && features.data?.lorawanSimulatorEnabled === true;

  if (device.isPending) {
    return <LoadingBlock label={tDevices("loadingDevice")} />;
  }

  if (device.isError) {
    return (
      <>
        <PageHeader title={tDevices("detailFallback")} />
        <ErrorBlock error={device.error} onRetry={() => void device.refetch()} />
        <p className="mt-3 text-sm">
          <Link to="/devices" className="text-brand-strong underline underline-offset-2">
            {tDevices("backToList")}
          </Link>
        </p>
      </>
    );
  }

  const asset = assets.data?.data.find((item) => item.id === device.data.assetId);
  // What the device measures decides which of the asset's ranges is the
  // operative one, and everything the page says about units follows from it.
  const measuresDistance = device.data.type === "DISTANCE_LEVEL";
  const measuresCurrent = device.data.type === "CURRENT";
  const measuresPresence = device.data.type === "PRESENCE";
  const measuresLeak = device.data.type === "WATER_LEAK";
  const measuresDoor = device.data.type === "MAGNETIC_CONTACT";
  const isController = device.data.type === "IOT_CONTROLLER";
  const binaryBit = measuresPresence || measuresLeak || measuresDoor;
  // Climate comes from the model. A thermometer still shows temperature when
  // the registry has no climate field; a contact or leak bit does not, unless
  // that model is marked as measuring it. A current clamp is not climate:
  // optional cable temperature is a separate line (ADR-102).
  const climate = climateOf(device.data.manufacturer, device.data.model);
  const primaryIsClimate = !isController && !binaryBit && !measuresDistance && !measuresCurrent;
  const measuresTemperature = climate !== undefined || primaryIsClimate;
  const measuresHumidity = climate ? climate.humidity : primaryIsClimate;
  const climateBesidePrimary = measuresTemperature && !primaryIsClimate && !isController;
  const rangeKind = measuresCurrent ? "current" : measuresDistance ? "distance" : "temperature";
  const targetRange = rangeOf(asset, rangeKind);
  const formatTargetRange = measuresCurrent
    ? formatCurrentRange
    : measuresDistance
      ? formatDistanceRange
      : formatTemperatureRange;

  return (
    <>
      <PageHeader
        title={device.data.assetName ?? tDevices("noAsset")}
        description={<span className="font-mono text-xs">{formatDevEui(device.data.devEui)}</span>}
        action={
          canEdit ? (
            <div className="flex flex-wrap gap-2">
              {canSimulate ? (
                <Button variant="secondary" onClick={() => setSimulating(true)}>
                  {tDevices("simulate.action")}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => setEditing((value) => !value)}>
                {editing ? t("cancel") : t("edit")}
              </Button>
              {device.data.status === "INACTIVE" ? null : (
                <Button variant="danger" onClick={() => setConfirmingDeactivation(true)}>
                  {t("deactivate")}
                </Button>
              )}
            </div>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <DeviceStatusBadge value={device.data.status} />
        <ConnectivityBadge value={device.data.connectivity} />
        <RangeStateBadge value={device.data.rangeState} />
      </div>

      {editing ? (
        <div className="mb-4">
          <DeviceEditForm device={device.data} onDone={() => setEditing(false)} />
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title={tDevices("details")} />
          <CardBody>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <Detail label={tDevices("fieldSite")} value={device.data.siteName ?? tDevices("unassigned")} />
              <Detail label={tDevices("fieldAsset")} value={device.data.assetName ?? tDevices("unassigned")} />
              {/* A presence sensor is judged against a calendar rather than
                  a pair of numbers, so it gets the hours in place of the
                  range it does not have (ADR-069). */}
              {isController ? (
                <Detail
                  label={tDevices("targetRange")}
                  value={tDevices("channels.title")}
                />
              ) : measuresPresence ? (
                <Detail
                  label={tDevices("expectedOccupancy")}
                  value={asset ? formatOccupancy(asset) : tDevices("noAssetAssigned")}
                />
              ) : measuresLeak ? (
                <Detail label={tDevices("targetRange")} value={tDevices("leakState")} />
              ) : measuresDoor ? (
                <Detail label={tDevices("targetRange")} value={tDevices("doorState")} />
              ) : (
                <Detail
                  label={tDevices("targetRange")}
                  value={formatTargetRange(targetRange?.min, targetRange?.max)}
                />
              )}
              <Detail label={tDevices("model")} value={device.data.model} />
              <Detail label={tDevices("manufacturer")} value={device.data.manufacturer} />
              <Detail label={tDevices("supplier")} value={device.data.supplier ?? "—"} />
              <Detail label={tDevices("network")} value={device.data.provider} />
              <Detail
                label={tDevices("table.lastReading")}
                value={
                  device.data.lastSeenAt
                    ? `${formatRelative(device.data.lastSeenAt)} (${formatDateTime(device.data.lastSeenAt)})`
                    : tDevices("connectivity.NEVER_SEEN")
                }
              />
              {/* Only the readings this sensor makes. An empty "Humidity —"
                  row reads as a fault rather than as a sensor that was never
                  asked to measure humidity. Controllers list channels below. */}
              {isController ? null : measuresPresence ? (
                <Detail label={tDevices("occupancy")} value={formatOccupied(device.data.lastOccupied)} />
              ) : measuresLeak ? (
                <Detail label={tDevices("leakState")} value={formatLeaking(device.data.lastLeaking)} />
              ) : measuresDoor ? (
                <Detail label={tDevices("doorState")} value={formatDoorOpen(device.data.lastDoorOpen)} />
              ) : measuresDistance ? (
                <Detail
                  label={tDevices("distanceToContents")}
                  value={formatDistance(device.data.lastDistanceMm)}
                />
              ) : measuresCurrent ? (
                <Detail
                  label={tDevices("table.current")}
                  value={formatCurrent(device.data.lastCurrentA)}
                />
              ) : null}
              {measuresCurrent && device.data.lastTemperatureC !== null ? (
                <Detail
                  label={tDevices("cableTemperature")}
                  value={formatTemperature(device.data.lastTemperatureC)}
                />
              ) : null}
              {measuresTemperature ? (
                <Detail
                  label={tDevices("table.temperature")}
                  value={formatTemperature(device.data.lastTemperatureC)}
                />
              ) : null}
              {measuresHumidity ? (
                <Detail
                  label={tDevices("table.humidity")}
                  value={formatHumidity(device.data.lastHumidityPercent)}
                />
              ) : null}
              {isController || measuresCurrent ? null : (
                <Detail label={tDevices("table.battery")} value={formatBattery(device.data.lastBatteryPercent)} />
              )}
            </dl>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title={isController ? tDevices("channels.title") : tDevices("readingsOverTime")}
            description={
              isController
                ? tDevices("chart.descController")
                : measuresPresence
                  ? tDevices("chart.descPresence")
                  : measuresLeak
                    ? tDevices("chart.descLeak")
                      : measuresDoor
                        ? tDevices("chart.descDoor")
                        : measuresCurrent
                          ? `${tDevices("chart.descCurrent")}${
                              targetRange
                                ? ` ${tDevices("chart.descWithRange", {
                                    range: formatTargetRange(targetRange.min, targetRange.max),
                                  })}`
                                : ""
                            }`
                          : targetRange
                        ? `${tDevices("chart.descWithRange", {
                            range: formatTargetRange(targetRange.min, targetRange.max),
                          })}${measuresDistance ? tDevices("chart.descDistanceRising") : ""}`
                        : tDevices("chart.descNoRange")
            }
            action={
              isController ? null : (
                <fieldset className="flex gap-1">
                  <legend className="sr-only">{tDevices("periodLegend")}</legend>
                  {PERIODS.map((period) => (
                    <Button
                      key={period.hours}
                      size="sm"
                      variant={hours === period.hours ? "primary" : "secondary"}
                      aria-pressed={hours === period.hours}
                      onClick={() => setHours(period.hours)}
                    >
                      {period.label}
                    </Button>
                  ))}
                </fieldset>
              )
            }
          />
          <CardBody className={isController ? "" : "px-2"}>
            {isController ? (
              <ControllerChannelsTable
                deviceId={device.data.id}
                channels={device.data.channels}
                canEdit={canEdit}
              />
            ) : (
              <>
                {chartPending ? <LoadingBlock label={tDevices("loadingReadings")} /> : null}

                {chartError ? (
                  <ErrorBlock error={chartError} onRetry={() => void chartRefetch()} />
                ) : null}

                {!chartPending && !chartError ? (
                  <Suspense fallback={<LoadingBlock label={tDevices("loadingChart")} />}>
                    <TelemetryChart
                      readings={chartReadings}
                      targetRange={targetRange}
                      humidity={measuresHumidity}
                      variant={
                        measuresPresence
                          ? "presence"
                          : measuresLeak
                            ? "leak"
                            : measuresDoor
                              ? "door"
                              : measuresDistance
                                ? "distance"
                                : measuresCurrent
                                  ? "current"
                                  : "climate"
                      }
                    />
                  </Suspense>
                ) : null}
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {climateBesidePrimary ? (
        <div className="mt-4">
          <Card>
            <CardHeader
              title={
                measuresHumidity ? tDevices("chart.climate") : tDevices("chart.temperature")
              }
              description={
                targetRange
                  ? `${tDevices("chart.descClimate")} ${tDevices("chart.descWithRange", {
                      range: formatTemperatureRange(targetRange.min, targetRange.max),
                    })}`
                  : tDevices("chart.descClimate")
              }
            />
            <CardBody className="px-2">
              {chartPending ? <LoadingBlock label={tDevices("loadingChart")} /> : null}
              {chartError ? (
                <ErrorBlock error={chartError} onRetry={() => void chartRefetch()} />
              ) : null}
              {!chartPending && !chartError ? (
                <Suspense fallback={<LoadingBlock label={tDevices("loadingChart")} />}>
                  <TelemetryChart
                    readings={chartReadings}
                    targetRange={targetRange}
                    humidity={measuresHumidity}
                    variant="climate"
                  />
                </Suspense>
              ) : null}
            </CardBody>
          </Card>
        </div>
      ) : null}

      {isController ? null : (
        <div className="mt-4">
          <Card>
            <CardHeader
              title={tDevices("chart.coverage")}
              description={tDevices("chart.descCoverage")}
            />
            <CardBody className="px-2">
              {chartPending ? <LoadingBlock label={tDevices("loadingChart")} /> : null}
              {chartError ? (
                <ErrorBlock error={chartError} onRetry={() => void chartRefetch()} />
              ) : null}
              {!chartPending && !chartError ? (
                <Suspense fallback={<LoadingBlock label={tDevices("loadingChart")} />}>
                  <CoverageChart readings={chartReadings} />
                </Suspense>
              ) : null}
            </CardBody>
          </Card>
        </div>
      )}

      <div className="mt-4">
        <InstallationCard deviceId={device.data.id} />
      </div>

      {isController ? null : (
        <div className="mt-4">
          <Card>
            <CardHeader title={tDevices("latestReadings")} description={tDevices("latestReadingsHint")} />
            <CardBody className="px-0 py-0">
              <DataTable caption={tDevices("readingsCaption")}>
                <thead>
                  <tr>
                    <Th>{tDevices("colTime")}</Th>
                    {measuresPresence ? (
                      <Th>{tDevices("occupancy")}</Th>
                    ) : measuresLeak ? (
                      <Th>{tDevices("leakState")}</Th>
                    ) : measuresDoor ? (
                      <Th>{tDevices("doorState")}</Th>
                    ) : measuresDistance ? (
                      <Th numeric>{tDevices("table.distance")}</Th>
                    ) : measuresCurrent ? (
                      <Th numeric>{tDevices("table.current")}</Th>
                    ) : (
                      <Th numeric>{tDevices("table.temperature")}</Th>
                    )}
                    {climateBesidePrimary ? (
                      <Th numeric>{tDevices("table.temperature")}</Th>
                    ) : null}
                    {measuresHumidity ? (
                      <Th numeric>{tDevices("table.humidity")}</Th>
                    ) : null}
                    {measuresCurrent ? null : <Th numeric>{tDevices("table.battery")}</Th>}
                    <Th numeric>{tDevices("colRssi")}</Th>
                    <Th numeric>{tDevices("colSnr")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {(telemetry.data?.data ?? []).length === 0 ? (
                    <EmptyRow
                      colSpan={
                        (measuresCurrent ? 4 : 5) +
                        (climateBesidePrimary ? 1 : 0) +
                        (measuresHumidity ? 1 : 0)
                      }
                    >
                      {tDevices("emptyReadings")}
                    </EmptyRow>
                  ) : (
                    (telemetry.data?.data ?? []).slice(0, 20).map((reading) => (
                      <tr key={reading.id}>
                        <Td>{formatDateTime(reading.measuredAt)}</Td>
                        {measuresPresence ? (
                          <Td>{formatOccupied(reading.occupied)}</Td>
                        ) : measuresLeak ? (
                          <Td>{formatLeaking(reading.leaking)}</Td>
                        ) : measuresDoor ? (
                          <Td>{formatDoorOpen(reading.doorOpen)}</Td>
                        ) : measuresDistance ? (
                          <Td numeric>{formatDistance(reading.distanceMm)}</Td>
                        ) : measuresCurrent ? (
                          <Td numeric>{formatCurrent(reading.currentA)}</Td>
                        ) : (
                          <Td numeric>{formatTemperature(reading.temperatureC)}</Td>
                        )}
                        {climateBesidePrimary ? (
                          <Td numeric>{formatTemperature(reading.temperatureC)}</Td>
                        ) : null}
                        {measuresHumidity ? (
                          <Td numeric>{formatHumidity(reading.humidityPercent)}</Td>
                        ) : null}
                        {measuresCurrent ? null : (
                          <Td numeric>{formatBattery(reading.batteryPercent)}</Td>
                        )}
                        <Td numeric>{reading.rssi ?? "—"}</Td>
                        <Td numeric>
                          {reading.snr === null || reading.snr === undefined
                            ? "—"
                            : reading.snr.toFixed(1)}
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </DataTable>
            </CardBody>
          </Card>
        </div>
      )}

      <DeactivateDialog
        device={device.data}
        open={confirmingDeactivation}
        onClose={() => setConfirmingDeactivation(false)}
      />

      {canSimulate ? (
        <SimulateUplinkDialog
          device={device.data}
          open={simulating}
          onClose={() => setSimulating(false)}
        />
      ) : null}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </>
  );
}

function ControllerChannelsTable({
  deviceId,
  channels,
  canEdit,
}: {
  deviceId: string;
  channels: readonly DeviceChannel[];
  canEdit: boolean;
}) {
  const { t: tDevices } = useTranslation("devices");
  const [editing, setEditing] = useState<DeviceChannel | null>(null);

  if (channels.length === 0) {
    return <p className="text-sm text-ink-muted">{tDevices("channels.empty")}</p>;
  }

  return (
    <>
      <p className="mb-3 text-sm text-ink-muted">{tDevices("channels.modbusHint")}</p>
      <DataTable caption={tDevices("channels.title")}>
        <thead>
          <tr>
            <Th>{tDevices("channels.key")}</Th>
            <Th>{tDevices("channels.kind")}</Th>
            <Th>{tDevices("channels.label")}</Th>
            <Th>{tDevices("channels.lastValue")}</Th>
            <Th>{tDevices("channels.lastMeasured")}</Th>
            {canEdit ? <Th>{tDevices("channels.edit")}</Th> : null}
          </tr>
        </thead>
        <tbody>
          {channels.map((channel) => (
            <tr key={channel.id}>
              <Td>
                <span className="font-mono text-xs">{channel.key}</span>
                {channel.alertEnabled ? (
                  <span className="ml-2 text-xs text-warn">{tDevices("channelAlertMark")}</span>
                ) : null}
              </Td>
              <Td>{CHANNEL_KIND_LABELS[channel.kind]}</Td>
              <Td>{channel.label ?? "—"}</Td>
              <Td>{formatChannelValue(channel, tDevices)}</Td>
              <Td>
                {channel.lastMeasuredAt
                  ? formatDateTime(channel.lastMeasuredAt)
                  : tDevices("channels.never")}
              </Td>
              {canEdit ? (
                <Td>
                  <Button size="sm" variant="secondary" onClick={() => setEditing(channel)}>
                    {tDevices("channels.edit")}
                  </Button>
                </Td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </DataTable>
      <ChannelConfigDialog
        deviceId={deviceId}
        channel={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        canControl={canEdit}
      />
    </>
  );
}

function formatChannelValue(
  channel: DeviceChannel,
  tDevices: (key: string) => string,
): string {
  if (channel.lastBool !== null) {
    return channel.lastBool ? tDevices("channels.on") : tDevices("channels.off");
  }
  if (channel.lastText !== null) {
    return channel.lastText;
  }
  if (channel.lastNumber !== null) {
    const unit = channel.unit ? ` ${channel.unit}` : "";
    return `${channel.lastNumber}${unit}`;
  }
  return "—";
}

/**
 * The band to draw on the chart, or nothing when the asset is monitored
 * without a target range (ADR-045). Both ends are required: half a band would
 * be drawn somewhere arbitrary.
 *
 * Which of the asset's ranges applies comes from what the device measures,
 * not from the asset's type. An asset can carry both, and a thermometer on a
 * tank is still a thermometer.
 */
function rangeOf(
  asset: Asset | undefined,
  kind: "temperature" | "distance" | "current",
): { min: number; max: number } | undefined {
  if (!asset) {
    return undefined;
  }

  const min =
    kind === "current"
      ? asset.targetCurrentMinA
      : kind === "distance"
        ? asset.targetDistanceMinMm
        : asset.targetTempMinC;
  const max =
    kind === "current"
      ? asset.targetCurrentMaxA
      : kind === "distance"
        ? asset.targetDistanceMaxMm
        : asset.targetTempMaxC;

  if (min === null || max === null) {
    return undefined;
  }

  return { min, max };
}

function DeactivateDialog({
  device,
  open,
  onClose,
}: {
  device: Device;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("pages");
  const { t: tDevices } = useTranslation("devices");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    ...deactivateDeviceMutation(client, device.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onClose();
      await navigate("/devices");
    },
  });

  return (
    <ConfirmDialog
      open={open}
      title={t("deactivateTitle")}
      confirmLabel={t("deactivate")}
      destructive
      pending={mutation.isPending}
      onConfirm={() => mutation.mutate()}
      onCancel={() => {
        if (!mutation.isPending) {
          onClose();
        }
      }}
    >
      <p>
        {tDevices("deactivateBody", { devEui: formatDevEui(device.devEui) })}
      </p>
      <p className="mt-2">{tDevices("deactivateKept")}</p>
      {mutation.isError ? (
        <p className="mt-2 font-medium text-danger">{mutation.error.message}</p>
      ) : null}
    </ConfirmDialog>
  );
}
